import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { db } from '../../../firebase/config';
import { 
    collection, 
    getDocs, 
    query, 
    where,
    collectionGroup
} from 'firebase/firestore';
import { useReglasComision } from '../../hooks/useReglasComision';
import { saveToFirestore } from '../../../firebase/cleanPayload';
import { calculatePolicyStatus } from '../formularioEmisionPolizas/policyUtils';

// Importamos el Contexto de Datos Maestros y las Utilidades Lógicas
import { useSegurosData } from '../../../context/SegurosDataContext';
import { 
    parseCurrency, 
    formatCurrencyForInput, 
    parsePercent, 
    normalizeString 
} from '../../../utils/segurosUtils';

/**
 * Hook de negocio optimizado para gestionar el estado de contratos de ART.
 * Incluye sincronización reactiva de comisiones entre Canal y Subcanal según Compañía.
 */
export const useArtForm = (clientData, initialData, onSuccess) => {
    const { dbMasters } = useSegurosData();

    const [formData, setFormData] = useState({
        fechaCarga: new Date().toISOString().split('T')[0],
        estadoPoliza: '', 
        nContrato: '', 
        tipoOperacion: '',
        subOperacion: '',
        inicioVigencia: '', 
        finVigencia: '',
        ciiu: '', 
        capitas: '', 
        masaSalarial: '', 
        alicuota: '',
        compania: '', 
        companiaId: '', 
        riesgo: 'ART', 
        riesgoId: '',  
        organizador: '', 
        pas: '',
        canal: '', 
        canalNombreId: '', 
        canalNombre: '', 
        subcanalNombreId: '', 
        subcanalNombre: '',
        vendedor: '', 
        suscriptor: '', 
        tipoConversion: '', 
        tieneContador: false, 
        contadorId: '', 
        contadorNombre: '',
        primaEmitida: '', 
        porcentajeComision: '', 
        comisionOrganizador: '', 
        comisionAsociado: '',
        descuentoOrganizador: '', 
        descuentoAsociado: '',
        fechaDespacho: '', 
        formaDespacho: '',
        observaciones: '',
        manualComisionOverride: false
    });

    const [dbSubcanales, setDbSubcanales] = useState([]);
    const [canalObj, setCanalObj] = useState(null);
    const [subcanalObj, setSubcanalObj] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // --- ESTADOS PARA VALIDACIÓN DE DUPLICADOS EN FIREBASE ---
    const [isValidatingContrato, setIsValidatingContrato] = useState(false);
    const [contratoDuplicado, setContratoDuplicado] = useState(null);

    const isHydrated = useRef(false);

    // Filtramos únicamente las aseguradoras de ART
    const companiasART = useMemo(() => {
        if (!dbMasters.companias) return [];
        return dbMasters.companias.filter(c => c.grupo === 'ART' || c.riesgo === 'ART');
    }, [dbMasters.companias]);

    // Calcula comisiones de acuerdos de forma directa
    const obtenerComisionAcuerdo = (companiaNombre, companiaIdInput) => {
        if (!companiaNombre || dbMasters.acuerdos.length === 0) return 0;
        const compBusqueda = normalizeString(companiaNombre);
        const artRiesgoId = dbMasters.riesgos.find(r => r.nombre?.toUpperCase() === 'ART')?.id || 'art';

        const acuerdo = dbMasters.acuerdos.find(a => {
            const matchCia = 
                (a.compania && normalizeString(a.compania) === compBusqueda) ||
                (a.companiaId && a.companiaId === companiaIdInput) ||
                (a.companiaId && normalizeString(a.companiaId) === compBusqueda);

            const riesgoNorm = normalizeString(a.riesgo);
            const matchRiesgo = 
                (a.riesgoId && a.riesgoId === artRiesgoId) || 
                ['art', 'riesgos del trabajo', 'riesgos de trabajo', 'riesgo de trabajo'].some(r => riesgoNorm.includes(r)) ||
                a.riesgoId === 'art';

            return matchCia && matchRiesgo;
        });

        return acuerdo ? (acuerdo.comision || acuerdo.porcentaje || 0) : 0;
    };

    // Resuelve el estado de póliza y número de contrato de forma unificada
    const resolverEstadoYContrato = (inicio, fin, estadoActual, contratoActual) => {
        const nuevoEstado = calculatePolicyStatus(inicio, fin, estadoActual);
        let nuevoContrato = contratoActual;

        if (!initialData?.id) {
            const mapping = { 
                'PENDIENTE DE EMISIÓN': 'PE-', 
                'PENDIENTE DE INSPECCIÓN': 'PI-', 
                'PROPUESTA INGRESADA': 'PIN-' 
            };
            const prefix = mapping[nuevoEstado];
            
            if (prefix) {
                if (!contratoActual.startsWith(prefix)) {
                    nuevoContrato = `${prefix}${Math.floor(10000 + Math.random() * 90000)}`;
                }
            } else if (contratoActual.includes('-')) {
                nuevoContrato = '';
            }
        }

        return { nuevoEstado, nuevoContrato };
    };

    // FUNCIÓN DE VALIDACIÓN DE N° DE CONTRATO DUPLICADO
    const validarContratoDuplicado = useCallback(async (numContratoEvaluado) => {
        const numClean = String(numContratoEvaluado || '').trim();

        if (!numClean || numClean.startsWith('PE-') || numClean.startsWith('PI-') || numClean.startsWith('PIN-')) {
            setContratoDuplicado(null);
            return null;
        }

        setIsValidatingContrato(true);
        try {
            const q = query(
                collectionGroup(db, 'contratos'),
                where('nContrato', '==', numClean)
            );

            const snap = await getDocs(q);

            if (!snap.empty) {
                const docDuplicado = snap.docs.find(d => {
                    if (initialData?.id && d.id === initialData.id) return false;
                    return true;
                });

                if (docDuplicado) {
                    const dataDup = docDuplicado.data();
                    const ape = dataDup.clienteApellido || '';
                    const nom = dataDup.clienteNombre || 'CLIENTE';
                    const clienteNombreCompleto = `${ape} ${nom}`.trim();
                    
                    const infoDuplicado = {
                        esDuplicado: true,
                        numContrato: numClean,
                        clienteNombre: clienteNombreCompleto,
                        compania: dataDup.compania || 'Aseguradora S/D',
                        estadoPoliza: dataDup.estadoPoliza || 'Vigente'
                    };

                    setContratoDuplicado(infoDuplicado);
                    setIsValidatingContrato(false);
                    return infoDuplicado;
                }
            }

            setContratoDuplicado(null);
            setIsValidatingContrato(false);
            return null;
        } catch (error) {
            console.error("Error al validar duplicidad de contrato de ART:", error);
            setIsValidatingContrato(false);
            return null;
        }
    }, [initialData?.id]);

    useEffect(() => {
        if (dbMasters.canales.length === 0 || isHydrated.current) return;

        const artRiesgo = dbMasters.riesgos.find(r => r.nombre?.toUpperCase() === 'ART' || r.id === 'art');
        const artRiesgoId = artRiesgo?.id || '';
        const artRiesgoNombre = artRiesgo?.nombre || 'ART';

        if (initialData) {
            const fechaCargaOriginal = initialData.fechaCarga || new Date().toISOString().split('T')[0];
            const compNormal = initialData.compania?.toUpperCase().trim();
            const foundCia = companiasART.find(c => c.nombre?.toUpperCase().trim() === compNormal || c.id === initialData.companiaId);
            const ciaId = foundCia ? foundCia.id : (initialData.companiaId || '');
            const ciaNombre = foundCia ? foundCia.nombre : (initialData.compania || '');

            const pctComision = initialData.porcentajeComision !== undefined 
                ? initialData.porcentajeComision 
                : obtenerComisionAcuerdo(ciaNombre, ciaId);

            const { nuevoEstado, nuevoContrato } = resolverEstadoYContrato(
                initialData.inicioVigencia, 
                initialData.finVigencia, 
                initialData.estadoPoliza, 
                initialData.nContrato || ''
            );

            setFormData(prev => ({ 
                ...prev, 
                ...initialData,
                fechaCarga: fechaCargaOriginal,
                estadoPoliza: nuevoEstado,
                nContrato: nuevoContrato,
                canal: initialData.canal || clientData?.canal || '',
                canalNombreId: initialData.canalNombreId || clientData?.canalNombreId || '',
                canalNombre: initialData.canalNombre || clientData?.canalNombre || '',
                subcanalNombreId: initialData.subcanalNombreId || clientData?.subcanalNombreId || '',
                subcanalNombre: initialData.subcanalNombre || clientData?.subcanalNombre || '',
                vendedor: initialData.vendedor || clientData?.vendedor || '',
                compania: ciaNombre,
                companiaId: ciaId,
                riesgo: artRiesgoNombre,
                riesgoId: artRiesgoId,
                tieneContador: !!(clientData?.tieneContador || clientData?.contadorId || initialData.tieneContador),
                contadorId: clientData?.contadorId || initialData.contadorId || '',
                contadorNombre: clientData?.contadorNombre || initialData.contadorNombre || '',
                tipoConversion: initialData.tipoConversion || '', 
                primaEmitida: formatCurrencyForInput(initialData.primaEmitida),
                masaSalarial: formatCurrencyForInput(initialData.masaSalarial),
                alicuota: initialData.alicuota || '',
                porcentajeComision: pctComision,
                comisionOrganizador: initialData.comisionOrganizador !== undefined ? String(initialData.comisionOrganizador).replace('.', ',') : '',
                comisionAsociado: initialData.comisionAsociado !== undefined ? String(initialData.comisionAsociado).replace('.', ',') : '',
                descuentoOrganizador: initialData.descuentoOrganizador !== undefined ? String(initialData.descuentoOrganizador).replace('.', ',') : '',
                descuentoAsociado: initialData.descuentoAsociado !== undefined ? String(initialData.descuentoAsociado).replace('.', ',') : '',
                cotizacionVinculadaId: initialData.cotizacionVinculadaId || initialData.cotizacionId || '',
                cotizacionVinculadaPath: initialData.cotizacionVinculadaPath || initialData.cotizacionPath || ''
            }));
            
            const activeCanalId = initialData.canalNombreId || clientData?.canalNombreId;
            if (activeCanalId) setCanalObj(dbMasters.canales.find(c => c.id === activeCanalId));
        } else if (clientData) {
            const compNormal = clientData.compania?.toUpperCase().trim();
            const foundCia = companiasART.find(c => c.nombre?.toUpperCase().trim() === compNormal || c.id === clientData.companiaId);
            const ciaId = foundCia ? foundCia.id : '';
            const ciaNombre = foundCia ? foundCia.nombre : '';

            setFormData(prev => ({ 
                ...prev, 
                canal: clientData.canal || '', 
                canalNombreId: clientData.canalNombreId || '', 
                canalNombre: clientData.canalNombre || '',
                subcanalNombreId: clientData.subcanalNombreId || '', 
                subcanalNombre: clientData.subcanalNombre || '',
                vendedor: clientData.vendedor || '', 
                ciiu: clientData.ciiu || '', 
                capitas: clientData.capitas || '',
                compania: ciaNombre,
                companiaId: ciaId,
                riesgo: artRiesgoNombre,
                riesgoId: artRiesgoId,
                masaSalarial: formatCurrencyForInput(clientData.masaSalarial),
                alicuota: clientData.alicuota || '',
                tieneContador: !!(clientData.tieneContador || clientData.contadorId), 
                contadorId: clientData.contadorId || '', 
                contadorNombre: clientData.contadorNombre || '',
                tipoConversion: '',
                porcentajeComision: obtenerComisionAcuerdo(ciaNombre, ciaId)
            }));
            if (clientData.canalNombreId) setCanalObj(dbMasters.canales.find(c => c.id === clientData.canalNombreId));
        }
        isHydrated.current = true;
    }, [initialData, clientData, dbMasters.canales, dbMasters.riesgos, dbMasters.acuerdos]);

    useEffect(() => {
        const fetchSubcanales = async () => {
            if (!formData.canalNombreId) { 
                setDbSubcanales([]); 
                setSubcanalObj(null);
                return; 
            }
            const q = query(collection(db, 'public/data/subcanales'), where('canalId', '==', formData.canalNombreId));
            const snap = await getDocs(q);
            const subs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setDbSubcanales(subs);
            if (formData.subcanalNombreId) {
                const foundSub = subs.find(s => s.id === formData.subcanalNombreId);
                if (foundSub) setSubcanalObj(foundSub);
            }
        };
        fetchSubcanales();
    }, [formData.canalNombreId]);

    // Reglas de comisión derivadas del Hook importado
    const { comisionCanal, comisionSubcanal, descuentoCanal, descuentoSubcanal } = useReglasComision(canalObj, subcanalObj, 'ART', formData.compania);

    // --- SINCRONIZACIÓN REACTIVA DE COMISIONES (CANAL + SUBCANAL) ---
    useEffect(() => {
        if (!isHydrated.current || formData.manualComisionOverride) return;

        const formatVal = (v) => (v !== '' && v !== null && v !== undefined ? String(v).replace('.', ',') : '');

        const targetComOrg = formatVal(comisionCanal);
        const targetComAsoc = formatVal(comisionSubcanal);
        const targetDescOrg = formatVal(descuentoCanal);
        const targetDescAsoc = formatVal(descuentoSubcanal);

        const updates = {};
        if (targetComOrg !== '' && formData.comisionOrganizador !== targetComOrg) updates.comisionOrganizador = targetComOrg;
        if (targetComAsoc !== '' && formData.comisionAsociado !== targetComAsoc) updates.comisionAsociado = targetComAsoc;
        if (targetDescOrg !== '' && formData.descuentoOrganizador !== targetDescOrg) updates.descuentoOrganizador = targetDescOrg;
        if (targetDescAsoc !== '' && formData.descuentoAsociado !== targetDescAsoc) updates.descuentoAsociado = targetDescAsoc;

        if (Object.keys(updates).length > 0) {
            setFormData(prev => ({ ...prev, ...updates }));
        }
    }, [comisionCanal, comisionSubcanal, descuentoCanal, descuentoSubcanal, formData.manualComisionOverride]);

    // Listado dinámico de organizadores/productores dependientes
    const companiaLists = useMemo(() => {
        if (!formData.compania || companiasART.length === 0) return { organizadores: [], productores: [] };
        const cia = companiasART.find(c => c.nombre === formData.compania);
        if (!cia?.codigos) return { organizadores: [], productores: [] };
        return {
            organizadores: [...new Set(cia.codigos.map(x => x.organizador).filter(Boolean))],
            productores: [...new Set(cia.codigos.map(x => x.pas).filter(Boolean))]
        };
    }, [formData.compania, companiasART]);

    const handleCanalTypeChange = useCallback((val) => {
        setFormData(prev => ({ 
            ...prev, 
            canal: val, 
            canalNombreId: '', 
            canalNombre: '', 
            subcanalNombreId: '', 
            subcanalNombre: '', 
            comisionOrganizador: '', 
            comisionAsociado: '', 
            descuentoOrganizador: '', 
            descuentoAsociado: '',
            manualComisionOverride: false
        }));
        setCanalObj(null);
        setSubcanalObj(null);
    }, []);

    // Aplica las reglas automáticas cuando el usuario cambia de Canal
    const handleCanalIdChange = (id) => {
        const selectedObj = dbMasters.canales.find(c => c.id === id);
        setCanalObj(selectedObj || null);
        setSubcanalObj(null);

        let comOrg = selectedObj?.artComision !== undefined ? selectedObj.artComision : '';
        let descOrg = selectedObj?.artDescuento !== undefined ? selectedObj.artDescuento : '';

        if (formData.compania && selectedObj?.artEspeciales) {
            const reglaC = selectedObj.artEspeciales.find(r => r.compania === formData.compania);
            if (reglaC) comOrg = reglaC.porcentaje;
        }

        setFormData(prev => ({ 
            ...prev, 
            canalNombreId: id, 
            canalNombre: selectedObj?.nombreDisplay || selectedObj?.nombre || '', 
            subcanalNombreId: '', 
            subcanalNombre: '', 
            comisionOrganizador: comOrg !== '' ? String(comOrg).replace('.', ',') : '', 
            comisionAsociado: '', 
            descuentoOrganizador: descOrg !== '' ? String(descOrg).replace('.', ',') : '', 
            descuentoAsociado: '',
            manualComisionOverride: false
        }));
    };

    // Aplica las reglas automáticas cuando el usuario cambia de Subcanal (Actualiza Subcanal Y Canal simultáneamente)
    const handleSubcanalIdChange = (id) => {
        const selectedObj = dbSubcanales.find(s => s.id === id);
        setSubcanalObj(selectedObj || null);

        let comAsoc = selectedObj?.comision !== undefined ? selectedObj.comision : '';
        let descAsoc = selectedObj?.descuento !== undefined ? selectedObj.descuento : '';
        let comCanal = '';
        let descCanal = '';

        if (formData.compania && selectedObj?.artReglas) {
            const reglaS = selectedObj.artReglas.find(r => r.compania === formData.compania);
            if (reglaS) {
                comAsoc = reglaS.distSub !== undefined ? reglaS.distSub : comAsoc;
                descAsoc = reglaS.descSub !== undefined ? reglaS.descSub : descAsoc;
                comCanal = reglaS.distCanal !== undefined ? reglaS.distCanal : '';
                descCanal = reglaS.descCanal !== undefined ? reglaS.descCanal : '';
            }
        }

        setFormData(prev => ({ 
            ...prev, 
            subcanalNombreId: id, 
            subcanalNombre: selectedObj ? selectedObj.nombre : '',
            // Si la regla del subcanal define una distribución para el Canal, se actualiza inmediatamente
            ...(comCanal !== '' ? { comisionOrganizador: String(comCanal).replace('.', ',') } : {}),
            ...(descCanal !== '' ? { descuentoOrganizador: String(descCanal).replace('.', ',') } : {}),
            comisionAsociado: comAsoc !== '' ? String(comAsoc).replace('.', ',') : '0',
            descuentoAsociado: descAsoc !== '' ? String(descAsoc).replace('.', ',') : '',
            manualComisionOverride: false
        }));
    };

    // Manejador centralizado de cambios de formulario
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = type === 'checkbox' ? checked : value;
        
        if (name === 'nContrato' && !['PENDIENTE DE EMISIÓN', 'PENDIENTE DE INSPECCIÓN', 'PROPUESTA INGRESADA'].includes(formData.estadoPoliza)) {
            finalValue = value.replace(/[^0-9]/g, '');
        }

        if (name === 'nContrato') {
            setContratoDuplicado(null);
        }
        
        setFormData(prev => {
            const nextState = { ...prev, [name]: finalValue };

            if (name === 'companiaId') {
                const selectedObj = companiasART.find(c => c.id === value);
                nextState.compania = selectedObj ? selectedObj.nombre : '';
                nextState.porcentajeComision = obtenerComisionAcuerdo(nextState.compania, value);
                nextState.organizador = '';
                nextState.pas = '';
                nextState.manualComisionOverride = false;
            } else if (name === 'compania') {
                const selectedObj = companiasART.find(c => c.nombre?.toUpperCase() === value.toUpperCase() || c.id === value);
                nextState.companiaId = selectedObj ? selectedObj.id : '';
                if (selectedObj) nextState.compania = selectedObj.nombre;
                nextState.porcentajeComision = obtenerComisionAcuerdo(nextState.compania, nextState.companiaId);
                nextState.organizador = '';
                nextState.pas = '';
                nextState.manualComisionOverride = false;
            }

            if (name === 'inicioVigencia' || name === 'finVigencia' || name === 'estadoPoliza') {
                const { nuevoEstado, nuevoContrato } = resolverEstadoYContrato(
                    nextState.inicioVigencia,
                    nextState.finVigencia,
                    nextState.estadoPoliza,
                    nextState.nContrato
                );
                nextState.estadoPoliza = nuevoEstado;
                nextState.nContrato = nuevoContrato;
            }
            
            return nextState;
        });
    };

    const handlePercentChange = (e) => {
        const { name, value } = e.target;
        let val = value.replace(/\./g, ',').replace(/[^0-9,]/g, '');
        const parts = val.split(',');
        if (parts.length > 2) val = parts[0] + ',' + parts.slice(1).join('');
        
        setFormData(prev => ({ 
            ...prev, 
            [name]: name === 'porcentajeComision' ? parsePercent(val) : val,
            manualComisionOverride: ['comisionOrganizador', 'comisionAsociado', 'descuentoOrganizador', 'descuentoAsociado'].includes(name) 
                ? true 
                : prev.manualComisionOverride
        }));
    };

    const handleCurrencyChange = (e, field) => {
        let val = e.target.value.replace(/\D/g, '');
        if (val) {
            const num = Number(val) / 100;
            setFormData(prev => ({ ...prev, [field]: formatCurrencyForInput(num) }));
        } else setFormData(prev => ({ ...prev, [field]: '' }));
    };

    const handleSave = async (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (!formData.compania || !formData.tipoConversion || !formData.nContrato || !formData.vendedor) return false;

        const duplicadoCheck = await validarContratoDuplicado(formData.nContrato);
        if (duplicadoCheck) {
            alert(`⛔ No se puede guardar: El contrato N° ${formData.nContrato} ya se encuentra cargado a nombre de ${duplicadoCheck.clienteNombre} (${duplicadoCheck.compania}).`);
            return false;
        }

        setIsSubmitting(true);
        try {
            const nombreFinal = (clientData?.nombre || formData.clienteNombre || '').trim().toUpperCase();
            const apellidoFinal = (clientData?.persona === 'JURIDICA' ? '' : (clientData?.apellido || formData.clienteApellido || '')).trim().toUpperCase();
            
            const payload = {
                ...formData,
                type: 'contratos',
                moneda: "$", 
                capitas: parseInt(formData.capitas) || 0,
                masaSalarial: parseCurrency(formData.masaSalarial),
                primaEmitida: parseCurrency(formData.primaEmitida),
                alicuota: parsePercent(formData.alicuota),
                porcentajeComision: Number(formData.porcentajeComision) || 0,
                comisionOrganizador: parsePercent(formData.comisionOrganizador),
                comisionAsociado: parsePercent(formData.comisionAsociado),
                descuentoOrganizador: parsePercent(formData.descuentoOrganizador),
                descuentoAsociado: parsePercent(formData.descuentoAsociado),
                clienteId: clientData.id,
                clienteNombre: nombreFinal,
                clienteApellido: apellidoFinal,
                clienteCuit: clientData.cuit || formData.clienteCuit || '',
                clienteDni: clientData.dni || formData.clienteDni || '',
                cotizacionVinculadaId: formData.cotizacionVinculadaId || initialData?.id || '',
                cotizacionVinculadaPath: formData.cotizacionVinculadaPath || initialData?.path || ''
            };
            
            delete payload.cotizacionId;
            delete payload.cotizacionPath;
            delete payload.numeroCotizacion;
            delete payload.nContratoVinculado;
            delete payload.manualComisionOverride;
            if (payload.clienteNombreCompleto) delete payload.clienteNombreCompleto;

            const path = `public/data/clientes/${clientData.id}/contratos`;
            const savedContract = await saveToFirestore(path, payload, initialData?.id ? initialData.id : null);

            const resultData = {
                id: savedContract.id, 
                contratoVinculadoId: savedContract.id, 
                nContrato: formData.nContrato || 'S/N', 
                nContratoVinculado: formData.nContrato || 'S/N', 
                clienteId: clientData.id,
                clienteNombre: nombreFinal,
                clienteApellido: apellidoFinal
            };

            if (onSuccess) onSuccess('Emitido', resultData);
        } catch (error) { 
            console.error("Error guardando contrato ART:", error); 
        } finally { 
            setIsSubmitting(false); 
        }
    };

    return { 
        state: { 
            formData, 
            dbMasters: {
                ...dbMasters,
                companias: companiasART 
            }, 
            dbSubcanales, 
            isSubmitting, 
            canalObj, 
            subcanalObj, 
            companiaLists,
            isValidatingContrato,
            contratoDuplicado
        }, 
        actions: { 
            handleChange, 
            handlePercentChange, 
            handleCanalTypeChange,
            handleCanalIdChange, 
            handleSubcanalIdChange, 
            handleCurrencyChange, 
            handleSave, 
            setFormData, 
            setSubcanalObj,
            validarContratoDuplicado
        } 
    };
};